import {
  type AdvancedFilterButtonHandleClickArgs, type BaseOptionsType,
  DEAL_CONDITION_KEY, filterActions, FilterContainer,
} from '@repo/advanced-filters';
import { message } from '@repo/ui';
import { FeatureConfigurationFeatureName } from '@repo/utils';
import { Drawer } from 'antd';
import { useClientReadableCriteria, useIsFeatureEnabled } from 'crm-atoms/hooks';
import { saveCriteria } from 'crm-atoms/utils';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';

export default function AdvancedFilterCriteriaBuilder({
  existingCriteriaId,
  open,
  onClose,
  onSave,
  title = 'Select Criteria',
  additionalConditionKeys,
  excludedDefaultKeys = [],
  saveButtonText = 'Add Criteria',
  allowEmpty = true,
  savedTriggerConditionKeys = [],
}: {
  existingCriteriaId?: number;
  open: boolean;
  onClose: () => void;
  onSave: (criteriaId: number | undefined) => void;
  title?: string;
  additionalConditionKeys?: BaseOptionsType[];
  excludedDefaultKeys?: string[],
  saveButtonText?: string,
  allowEmpty?: boolean,
  savedTriggerConditionKeys?: any[],
}) {
  const dispatch = useDispatch<any>();
  const { handleApplyFilter } = useClientReadableCriteria(existingCriteriaId, true);
  const [saving, setSaving] = useState<boolean>(false);
  const isDealsEnabled = useIsFeatureEnabled(FeatureConfigurationFeatureName.Deals);

  // Skipped when the caller already supplies Deal — smart views pass it in themselves.
  const conditionKeys = useMemo(() => {
    const keys = additionalConditionKeys?.length ? additionalConditionKeys : savedTriggerConditionKeys;
    if (!isDealsEnabled || keys.some((key) => key?.value === DEAL_CONDITION_KEY.value)) return keys;
    return [...keys, DEAL_CONDITION_KEY];
  }, [additionalConditionKeys, savedTriggerConditionKeys, isDealsEnabled]);

  const handleSave = useCallback(async (json: AdvancedFilterButtonHandleClickArgs) => {
    setSaving(true);
    try {
      if (json.conditions.length) {
        const updatedId = await dispatch(saveCriteria(json));
        onSave(updatedId);
        onClose();
      } else if (allowEmpty) {
        onSave(undefined);
        onClose();
      } else {
        throw new Error('No criteria selected');
      }
    } catch (e: any) {
      message.error(e.message ?? 'Something Went wrong updating the filter');
    } finally {
      setSaving(false);
    }
  }, [allowEmpty, dispatch, onClose, onSave]);

  useEffect(() => {
    if (open && existingCriteriaId) {
      handleApplyFilter();
    } else {
      dispatch(filterActions.clearFilter());
    }
  }, [dispatch, existingCriteriaId, handleApplyFilter, open]);

  return (
    <Drawer
      title={title}
      open={open}
      onClose={onClose}
      placement="bottom"
      height="80vh"
    >
      <FilterContainer
        additionalConditionKeys={conditionKeys}
        excludedDefaultKeys={excludedDefaultKeys}
        footerConfig={[
          {
            children: 'Cancel',
            handleClick: onClose,
          },
          {
            type: 'primary',
            children: saveButtonText,
            handleClick: handleSave,
            disabled: saving,
            loading: saving,
          },
        ]}
      />
    </Drawer>
  );
}
